import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import formStyles from '../../SSO/SsoConfigForm/index.module.scss';
import styles from '../../index.module.scss';
import { useTranslation } from 'react-i18next';
import { useForm } from 'antd/lib/form/Form';
import { Form, notification, Switch } from 'src/common';
import classNames from 'classnames';
import { useUpdateMFAMode } from 'src/models/company/Security/hooks';
import { SecurityView } from 'src/models/company/Security/types';
import { extractErrorMessage } from 'src/models/company/Security/helpers';
import { CognitoUser } from 'amazon-cognito-identity-js';

async function setTOTPMFAStatus(user: CognitoUser, enabled: boolean): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    user.setUserMfaPreference(
      null,
      {
        Enabled: enabled,
        PreferredMfa: enabled
      },
      err => {
        !err ? resolve() : reject(err);
      }
    );
  });
}

interface IFormValues {
  oneAccOption: boolean;
  allAccOption: boolean;
}

export interface IMfaDescProps {
  isAdmin: boolean;
  email: string;
  onSetCode: (code: string) => void;
  setView: (view: SecurityView) => void;
  cognitoUser?: CognitoUser;
  isMfaActive: boolean;
}

export const MfaDesc: FC<IMfaDescProps> = props => {
  const { isAdmin, email, onSetCode, setView, cognitoUser, isMfaActive } = props;
  const { t } = useTranslation();

  const [codeError, setCodeError] = useState<unknown>();

  useEffect(() => {
    if (codeError) {
      notification.error({
        message: t('Common.unknownError'),
        description: t('Common.unknownErrorDesc')
      });
    }
  }, [codeError, t]);

  const onActivateMfa = useCallback(async () => {
    if (!cognitoUser) {
      return;
    }
    try {
      const code: string = await new Promise((resolve, reject) => {
        cognitoUser.associateSoftwareToken({
          associateSecretCode: resolve,
          onFailure: reject
        });
      });
      onSetCode(code);
    } catch (err) {
      setCodeError(err);
    }
  }, [cognitoUser, onSetCode]);

  const [updateMfaMode] = useUpdateMFAMode();

  const onDeactivateMfa = useCallback(async () => {
    if (!cognitoUser) {
      return;
    }
    try {
      await setTOTPMFAStatus(cognitoUser, false);
      await updateMfaMode({
        variables: {
          email,
          mfaMode: null
        }
      });
      setView(SecurityView.greetMfaDeactivated);
    } catch (e) {
      if (extractErrorMessage(e) === 'User has not verified software token mfa') {
        // FIXME: https://github.com/aws-amplify/amplify-js/issues/3844
        //    I can do nothing. There is no way to disable it while TOTP setup is not completed.
        //    User have to complete it (or stat new one and complete) and only after this we will be able to disable it. Hah..
        //    So I just mute an exception so user will be able at least try to enable it again :/
        //    Maybe we could show a text with info about the issue?
      } else {
        notification.error({
          description: t('Common.unknownErrorDesc'),
          message: t('Common.unknownError')
        });
      }
    }
  }, [cognitoUser, email, setView, updateMfaMode, t]);

  const [form] = useForm();

  useLayoutEffect(() => {
    form.setFieldsValue({
      oneAccOption: isMfaActive
    });
  }, [form, isMfaActive, t]);

  const initialValues: IFormValues = useMemo(() => {
    return {
      oneAccOption: isMfaActive,
      allAccOption: false
    };
  }, [isMfaActive]);

  const onValuesChange = useCallback(
    (changedValues: Partial<IFormValues>) => {
      !!changedValues.oneAccOption ? onActivateMfa() : onDeactivateMfa();
    },
    [onActivateMfa, onDeactivateMfa]
  );

  return (
    <>
      <h2 className={styles.subTitle}>{t('Security.mfaAuth')}</h2>
      <div className={styles.content}>
        <p>{t('Security.securityMfaDesc1')}</p>
        {isAdmin && <p>{t('Security.securityMfaDesc2')}</p>}
        <p>{t('Security.securityMfaDesc3')}</p>
        <Form form={form} initialValues={initialValues} onValuesChange={onValuesChange} name={'mfaSwitcher'}>
          <Form.Item
            name="oneAccOption"
            label={t('Security.mfaOneAccOption')}
            className={classNames(formStyles.switchField, formStyles.mfaField)}
            valuePropName={'checked'}
          >
            <Switch size={'default'} className={formStyles.switch} />
          </Form.Item>
          {isAdmin && (
            <Form.Item
              name="allAccOption"
              label={t('Security.mfaAllAccOption')}
              className={classNames(formStyles.switchField, formStyles.mfaField, { [formStyles.isDisabled]: true })}
              valuePropName={'checked'}
            >
              <Switch size={'default'} className={formStyles.switch} disabled={true} />
            </Form.Item>
          )}
        </Form>
      </div>
    </>
  );
};

export default MfaDesc;
